import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { getWindCompassDegrees, normalizeNorthAngle } from '../../utils/wind-direction';

@Component({
  selector: 'app-wind-compass',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="inline-flex items-center gap-3 rounded-lg bg-primary-container/40 px-3 py-2 text-on-surface">
      @if (hasWindDirection()) {
        <div
          class="relative grid size-14 place-items-center rounded-full border border-outline/40 bg-surface shadow-sm"
          [attr.aria-label]="ariaLabel()"
          role="img"
        >
          <div
            data-testid="wind-compass-dial"
            class="absolute inset-1 rounded-full transition-transform duration-150 ease-out"
            [style.transform]="dialTransform()"
          >
            @for (tick of compassTicks; track tick) {
              <span
                class="absolute left-1/2 top-1/2 block h-1.5 w-px origin-[50%_0] bg-on-surface/60"
                [class.h-2.5]="tick % 90 === 0"
                [class.bg-on-surface]="tick % 90 === 0"
                [style.transform]="'rotate(' + tick + 'deg) translateY(-1.55rem)'"
              ></span>
            }
            <span class="absolute left-1/2 top-0 -translate-x-1/2 text-[10px] font-black leading-none text-on-surface">N</span>
            <span class="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] font-black leading-none text-on-surface">E</span>
            <span class="absolute bottom-0 left-1/2 -translate-x-1/2 text-[10px] font-black leading-none text-on-surface">S</span>
            <span class="absolute left-0 top-1/2 -translate-y-1/2 text-[10px] font-black leading-none text-on-surface">W</span>
          </div>
          <svg
            data-testid="wind-compass-arrow"
            class="relative z-10 size-8 text-primary drop-shadow-sm"
            viewBox="0 0 32 32"
            aria-hidden="true"
            [style.transform]="arrowTransform()"
          >
            <path
              d="M16 4 24 28 16 22 8 28 16 4Z"
              fill="currentColor"
              stroke="white"
              stroke-width="2"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      } @else {
        <span class="text-xs text-on-surface-variant">風向データなし</span>
      }
    </div>
  `,
})
export class WindCompassComponent {
  readonly compassTicks = Array.from({ length: 24 }, (_, index) => index * 15);
  readonly northAngle = input<number | undefined>(0);
  readonly windDirectionCode = input<number | undefined>(undefined);

  readonly normalizedNorthAngle = computed(() => normalizeNorthAngle(this.northAngle()));
  readonly hasWindDirection = computed(() => this.windDirectionCode() !== undefined);
  readonly compassDegrees = computed(() => getWindCompassDegrees(this.windDirectionCode(), this.normalizedNorthAngle()));
  readonly dialTransform = computed(() => `rotate(${this.normalizedNorthAngle()}deg)`);
  readonly arrowTransform = computed(() => `rotate(${this.compassDegrees()}deg)`);
  readonly ariaLabel = computed(() => `風向コンパス 北方位 ${this.normalizedNorthAngle()}度 風向角度 ${this.compassDegrees()}度`);
}
